import { computed, type Ref } from "vue";

const normalizeAttributeName = (name: unknown) =>
  String(name ?? "")
    .replace(/:\s*$/, "")
    .trim();

const normalizeAttributeValue = (options: unknown) => {
  if (!Array.isArray(options)) {
    const value = String(options ?? "").trim();
    return value || "N/A";
  }

  const values = options
    .map((option: unknown) => String(option ?? "").trim())
    .filter(Boolean);

  return values.length ? values.join(", ") : "N/A";
};

const FEATURED_ATTRIBUTE_NAMES = ["Lens width", "Bridge width", "Temple width"];

export function useProductAttributes(product: Ref<Record<string, any> | null>) {
  const productDetails = computed(() => {
    if (!Array.isArray(product.value?.attributes)) {
      return [];
    }

    return product.value.attributes
      .map((attribute: any) => ({
        name: normalizeAttributeName(attribute?.name),
        value: normalizeAttributeValue(attribute?.options),
      }))
      .filter((attribute: any) => attribute.name && attribute.name !== "Size");
  });

  const featuredProductDetails = computed(() =>
    productDetails.value.filter((attribute: any) =>
      FEATURED_ATTRIBUTE_NAMES.includes(attribute.name)
    )
  );

  const productDetailRows = computed(() => {
    const rows = productDetails.value.filter(
      (attribute: any) =>
        ![...FEATURED_ATTRIBUTE_NAMES, "Type"].includes(attribute.name)
    );

    // Surface the manufacturer model number as "Model-code", placed directly
    // above "Color-code". Skipped when the backend already provides one or
    // when product.model is empty.
    const modelCode = String(product.value?.model ?? "").trim();
    const hasModelCode = rows.some((r: any) =>
      /^model[\s-]?code$/i.test(r.name)
    );
    if (modelCode && !hasModelCode) {
      const entry = { name: "Model-code", value: modelCode };
      const colorIdx = rows.findIndex((r: any) =>
        /^colou?r[\s-]?code$/i.test(r.name)
      );
      if (colorIdx >= 0) rows.splice(colorIdx, 0, entry);
      else rows.unshift(entry);
    }

    // SKU pinned to the very bottom of the list.
    const sku = String(product.value?.sku ?? "").trim();
    const hasSku = rows.some((r: any) => /^sku$/i.test(r.name));
    if (sku && !hasSku) rows.push({ name: "SKU", value: sku });

    return rows;
  });

  return {
    featuredProductDetails,
    productDetailRows,
  };
}
